'use client'

import { useInputWrapper } from '../../../../hooks'
import { ReactElement, useCallback, useEffect, useState } from 'react'
import React from 'react'
import { toSingleValue } from '../_helpers/selectHelpers'
import { ComboboxListboxContext, useComboboxListbox } from '../_hooks/useComboboxListbox'
import { useNativeSelectChangeReset } from '../_hooks/useNativeSelectChangeReset'
import { useSyncDefaultValue } from '../_hooks/useSyncDefaultValue'
import { SelectTriggerButton } from './SelectTriggerButton'
import { SelectListbox } from './SelectListbox'
import { SelectOption } from './SelectOption'
import { exists } from '../../../../../../MyStichtingOpenHelpers/src/static'
import { HTMLSelectInputProps } from '../HTMLSelectInput'

/**
 * Single-select combobox for HTMLSelectInput. Keeps a hidden native select in sync for form
 * submission and reset. Renders a custom listbox with type-ahead, keyboard navigation, and optional clear.
 * Selecting an option closes the listbox.
 */
export const SingleSelectCombobox = ({
    className,
    options: optionsProp,
    disabled = false,
    clearable = false,
    defaultValue,
    error,
    name,
    id,
    'data-testid': dataTestId,
    required = false,
}: HTMLSelectInputProps): ReactElement => {
    const { classes } = useInputWrapper(false, disabled, error)
    const filteredOptions = optionsProp.filter((option) => !!option)

    const [selected, setSelected] = useState<string | number | null>(() => toSingleValue(defaultValue))

    const onActivateOption = useCallback(
        (value: string | number, comboboxListboxContext: ComboboxListboxContext) => {
            const option = filteredOptions.find((filteredOption) => filteredOption.value == value)
            if (option?.disabled) return

            setSelected(value)
            comboboxListboxContext.closeListbox()
        },
        [filteredOptions],
    )

    const {
        selectContainerRef,
        selectInputRef,
        listBoxTriggerRef,
        listboxId,
        isOpen,
        focusedIndex,
        openListbox,
        closeListbox,
        handleComboboxKeyDown,
        baseClasses,
        focusedOptionId,
    } = useComboboxListbox({
        disabled,
        className,
        classes: classes ?? '',
        filteredOptions,
        onActivateOption,
    })

    const selectOption = useCallback(
        (value: string | number) => {
            const option = filteredOptions.find((filteredOption) => filteredOption.value == value)
            if (option?.disabled) return

            setSelected(value)
            closeListbox()
        },
        [filteredOptions, closeListbox],
    )

    const handleClear = useCallback(() => {
        setSelected(null)
        requestAnimationFrame(() => {
            listBoxTriggerRef.current?.focus()
        })
    }, [])

    useSyncDefaultValue(defaultValue, setSelected, toSingleValue)

    useEffect(() => {
        const selectElement = selectInputRef.current
        if (!selectElement) return
        const stringValue = selected != null ? String(selected) : ''
        if (selectElement.value === stringValue) return
        selectElement.value = stringValue
        selectElement.dispatchEvent(new Event('change', { bubbles: true }))
    }, [selected, filteredOptions?.length])

    const handleChange = useCallback(() => {
        const selectElement = selectInputRef.current
        if (selectElement) setSelected(selectElement.value === '' ? null : selectElement.value)
    }, [])

    const handleReset = useCallback(() => {
        setSelected(toSingleValue(defaultValue))
    }, [defaultValue])

    useNativeSelectChangeReset(selectInputRef, { onChange: handleChange, onReset: handleReset }, [defaultValue])

    const handleOpenListbox = useCallback(() => {
        const initialIndex = selected != null ? filteredOptions.findIndex((option) => option.value == selected) : -1
        openListbox(() => (initialIndex >= 0 ? initialIndex : 0))
    }, [filteredOptions, selected, openListbox])

    const selectedArray = exists(selected) && selected !== '' ? [selected] : []

    return (
        <div ref={selectContainerRef} className="relative">
            <select
                ref={selectInputRef}
                name={name}
                id={id}
                required={required}
                disabled={disabled}
                style={{ display: 'none' }}
            >
                {clearable && <option value="" />}
                {filteredOptions.map((option) => (
                    <option key={`${option.value}_${option.label}`} value={option.value!}>
                        {option.label}
                    </option>
                ))}
            </select>
            <SelectTriggerButton
                ref={listBoxTriggerRef}
                baseClasses={baseClasses}
                disabled={disabled}
                isOpen={isOpen}
                onToggle={() => (isOpen ? closeListbox() : handleOpenListbox())}
                onKeyDown={handleComboboxKeyDown}
                id={id}
                dataTestId={dataTestId}
                selected={selectedArray}
                options={filteredOptions}
                listboxId={listboxId}
                ariaActivedescendant={focusedOptionId}
                required={required}
                clearable={clearable}
                onClear={handleClear}
            />
            {isOpen && (
                <SelectListbox id={listboxId} ariaLabelledby={id ? `${id}-label` : undefined}>
                    {filteredOptions.map((option, index) => (
                        <SelectOption
                            key={`${option.value}_${option.label}`}
                            option={option}
                            index={index}
                            listboxId={listboxId}
                            isSelected={selected == option.value}
                            isFocused={index === focusedIndex}
                            onActivate={selectOption}
                        />
                    ))}
                </SelectListbox>
            )}
            <div aria-live="polite" aria-atomic className="sr-only">
                {selected === null && ''}
                {exists(selected) &&
                    `${filteredOptions.find((option) => option.value == selected)?.label ?? selected} selected`}
            </div>
        </div>
    )
}
